/**
 * Landing.jsx v2 — Bootstrap 5 + full i18n ES/EN/CA
 *
 * Section order (canonical per canvas F0AT5K5Q4HY):
 *  1. Navbar
 *  2. Hero with parallax           ← CANVI 1
 *  3. Logos strip                  ← CANVI 2
 *  4. Features 6-card grid         ← CANVI 3
 *  5. Visual section A (img left)  ← CANVI 4
 *  6. Visual section B (img right) ← CANVI 4
 *  7. How it works (5 steps)
 *  8. Testimonials carousel        ← CANVI 5
 *  9. Pricing (3 plans)
 * 10. FAQ accordion
 * 11. Final CTA
 * 12. Footer
 *
 * Rules: all layout/responsiveness via Bootstrap classes; zero strings
 * hardcoded (every visible text via t('key')); 3 locales es/en/ca.
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart2, Layout, Brain, Download, DollarSign, HelpCircle, Globe,
  Sparkles, KeyRound, FolderKanban, Briefcase, Languages, ShieldCheck,
} from 'lucide-react'
import 'bootstrap/dist/css/bootstrap.min.css'
import '../styles/landing.css'
import AiSuiteSection from '../components/landing/AiSuiteSection'
import HeroMockup from '../components/landing/HeroMockup'
import { HubCards } from '../components/landing/HubCards'
import VisualMockupOps from '../components/landing/VisualMockupOps'
import VisualMockupAi from '../components/landing/VisualMockupAi'
import ComparisonSection from '../components/landing/ComparisonSection'
import BuiltForSection from '../components/landing/BuiltForSection'
import BuiltOnRow from '../components/landing/BuiltOnRow'
import LiveStatsBar from '../components/landing/LiveStatsBar'

// Enllaços a Freedolia (l'agència que fa Freedoliapp) al peu de la portada.
// La portada no usa el footer compartit (LandingFooter), així que fins ara
// no enllaçava ni els serveis ni el blog de freedolia.com.
const FREEDOLIA_LANGS = ['ca', 'es', 'en']
const FREEDOLIA_LINKS = [
  { key: 'fd_agency', path: '' },
  { key: 'fd_automation', path: 'serveis/automatitzacio' },
  { key: 'fd_chatbot', path: 'serveis/chatbot' },
  { key: 'fd_ai_consulting', path: 'serveis/consultoria_ia' },
  { key: 'fd_web', path: 'serveis/web_landing' },
  { key: 'fd_blog', path: 'blog' },
]

/* ─── Static data ─────────────────────────────────────────────────────────── */

const FEATURES = [
  { Icon: KeyRound,    titleKey: 'feature1.title', textKey: 'feature1.text' }, // BYOK
  { Icon: FolderKanban,titleKey: 'feature2.title', textKey: 'feature2.text' }, // pipeline end-to-end
  { Icon: Brain,       titleKey: 'feature3.title', textKey: 'feature3.text' }, // decision engine
  { Icon: Download,    titleKey: 'feature4.title', textKey: 'feature4.text' }, // SPAPI + imports
  { Icon: Briefcase,   titleKey: 'feature5.title', textKey: 'feature5.text' }, // PO + signatures
  { Icon: ShieldCheck, titleKey: 'feature6.title', textKey: 'feature6.text' }, // GDPR + multi-tenant
]

/* ─── Sub-components ──────────────────────────────────────────────────────── */

/**
 * Reusable visual + text section. Accepts either a pre-rendered `mockup`
 * JSX element (preferred — fully CSS/SVG-driven, scales infinitely) or
 * a legacy `imgSrc` PNG path. `reverse` flips the layout on md+.
 *
 * Supports an optional `bulletsKey` array (rendered as a 3-point checklist
 * below the description) and an optional `chipKey` (an eyebrow chip above
 * the title) so each section can hit its own commercial angle.
 */
function VisualSection({
  imgSrc,
  mockup,
  titleKey,
  textKey,
  chipKey,
  bulletKeys = [],
  reverse = false,
}) {
  const { t } = useTranslation()
  return (
    <section className="py-5">
      <div className="container">
        <div className={`row align-items-center g-5${reverse ? ' flex-md-row-reverse' : ''}`}>
          <div className="col-12 col-md-6">
            {mockup
              ? mockup
              : (
                <img
                  src={imgSrc}
                  alt={t(titleKey)}
                  className="img-fluid rounded-4"
                  style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}
                />
              )
            }
          </div>
          <div className="col-12 col-md-6">
            {chipKey && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(31,95,99,0.08)',
                color: 'var(--brand-1, #1F5F63)',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',
                marginBottom: 16,
              }}>
                {t(chipKey)}
              </span>
            )}
            <h2 className="display-6 fw-bold mb-4" style={{ color: '#1A1A2E', lineHeight: 1.1 }}>
              {t(titleKey)}
            </h2>
            <p className="lead text-muted" style={{ fontSize: 17, lineHeight: 1.55 }}>{t(textKey)}</p>
            {bulletKeys.length > 0 && (
              <ul style={{ marginTop: 18, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bulletKeys.map((k) => (
                  <li key={k} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    fontSize: 15, color: '#243333',
                  }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      background: 'rgba(63,191,154,0.16)', color: 'var(--success-ink, #2B7A66)',
                      fontSize: 12, fontWeight: 700, marginTop: 1,
                    }}>✓</span>
                    <span style={{ lineHeight: 1.5 }}>{t(k)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Main component ──────────────────────────────────────────────────────── */

export default function Landing() {
  const navigate     = useNavigate()
  const { t, i18n } = useTranslation()
  const fdLang = FREEDOLIA_LANGS.includes((i18n.language || '').slice(0, 2)) ? i18n.language.slice(0, 2) : 'ca'
  const [scrolled,     setScrolled]     = useState(false)
  const [yearly,       setYearly]       = useState(false)
  const [openFaq,      setOpenFaq]      = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  /* scroll watcher for navbar */
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [])

  /* CANVI 1 — parallax scroll */
  useEffect(() => {
    const el = document.querySelector('.hero-parallax-bg')
    const onScroll = () => {
      if (el) el.style.transform = `translateY(${window.scrollY * -0.15}px)`
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* ── Pricing plans ── */
  const PLANS = [
    {
      code: 'starter', name: t('landing.plans.starter.name'),
      monthly: 29, yearly: 24, desc: t('landing.plans.starter.desc'),
      features: [1,2,3,4,5,6,7].map(n => t(`landing.plans.starter.f${n}`)),
      cta: t('landing.plans.starter.cta'), highlight: false,
    },
    {
      code: 'growth', name: t('landing.plans.growth.name'),
      monthly: 79, yearly: 66, desc: t('landing.plans.growth.desc'),
      features: [1,2,3,4,5,6,7].map(n => t(`landing.plans.growth.f${n}`)),
      cta: t('landing.plans.growth.cta'), highlight: true,
      badge: t('landing.plans.growth.badge'),
    },
    {
      code: 'scale', name: t('landing.plans.scale.name'),
      monthly: 199, yearly: 166, desc: t('landing.plans.scale.desc'),
      features: [1,2,3,4,5,6,7].map(n => t(`landing.plans.scale.f${n}`)),
      cta: t('landing.plans.scale.cta'), highlight: false,
    },
  ]

  /* ── FAQ ── */
  const FAQS = [1,2,3,4,5,6].map(i => ({
    q: t(`landing.faq.q${i}.q`),
    a: t(`landing.faq.q${i}.a`),
  }))

  return (
    <div className="ld-page">

      {/* ── 1. NAVBAR ─────────────────────────────────────────────────────── */}
      <nav className={`ld-nav${scrolled ? ' ld-nav--scrolled' : ''}`}>
        <div className="container-fluid d-flex align-items-center justify-content-between h-100">
          <Link to="/" className="ld-nav__logo">
            <img
              src="/brand/freedoliapp/logo/logo_white.png"
              alt="Freedoliapp"
              onError={e => { e.target.src = '/brand/freedoliapp/logo/logo_master.png' }}
            />
          </Link>

          <div className="d-none d-md-flex align-items-center gap-4">
            <a href="#features" className="ld-nav__link">{t('landing.nav.features')}</a>
            <a href="#how"      className="ld-nav__link">{t('landing.nav.how')}</a>
            <a href="#pricing"  className="ld-nav__link">{t('landing.nav.pricing')}</a>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* language selector — cycles ES → EN → CA */}
            <button
              className="ld-btn ld-btn--ghost d-flex align-items-center gap-1"
              style={{ padding: '6px 10px', fontSize: 13 }}
              onClick={() => {
                const cycle = { ca: 'es', es: 'en', en: 'ca' }
                i18n.changeLanguage(cycle[i18n.language] || 'es')
              }}
              title={t('language.select')}
            >
              <Globe size={14} />
              {(i18n.language || 'ca').slice(0,2).toUpperCase()}
            </button>
            <button className="ld-btn ld-btn--ghost d-none d-md-inline-flex" onClick={() => navigate('/login')}>
              {t('landing.nav.login')}
            </button>
            <button className="ld-btn ld-btn--primary d-none d-md-inline-flex" onClick={() => navigate('/trial')}>
              {t('landing.nav.cta')}
            </button>
            {/* hamburger — mobile only */}
            <button
              className="d-md-none ld-btn ld-btn--ghost"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Menu"
              style={{ padding: '6px 10px', fontSize: 20, lineHeight: 1 }}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="d-md-none" style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: 'var(--brand-1)', borderTop: '1px solid rgba(255,255,255,0.12)',
            padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 4,
            zIndex: 999,
          }}>
            {[
              { href: '#features', label: t('landing.nav.features') },
              { href: '#how',      label: t('landing.nav.how') },
              { href: '#pricing',  label: t('landing.nav.pricing') },
            ].map(item => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 500,
                  textDecoration: 'none', padding: '10px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {item.label}
              </a>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="ld-btn ld-btn--ghost" style={{ flex: 1 }} onClick={() => { setMobileMenuOpen(false); navigate('/login') }}>
                {t('landing.nav.login')}
              </button>
              <button className="ld-btn ld-btn--primary" style={{ flex: 1 }} onClick={() => { setMobileMenuOpen(false); navigate('/trial') }}>
                {t('landing.nav.cta')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ── 2. HERO (CANVI 1 — parallax) ─────────────────────────────────── */}
      <section
        className="hero-section position-relative overflow-hidden"
        style={{ minHeight: '100vh', width: '100vw', marginLeft: 'calc(-1 * (100vw - 100%) / 2)' }}
      >
        {/* parallax background — sense w-100/h-100 per evitar conflicte amb top/bottom */}
        <div
          className="hero-parallax-bg position-absolute"
          style={{
            top: '-40%',
            bottom: '-40%',
            left: '-10%',
            right: '-10%',
            background: 'linear-gradient(135deg, var(--brand-1) 0%, #0d3a3d 50%, #1a2a2e 100%)',
            zIndex: 0,
            willChange: 'transform',
          }}
        />

        {/* glow top-right */}
        <div
          className="position-absolute"
          style={{
            width: 500, height: 500, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(110,203,195,0.15) 0%, transparent 70%)',
            top: -100, right: -100, zIndex: 0, pointerEvents: 'none',
          }}
        />

        {/* glow bottom-left */}
        <div
          className="position-absolute"
          style={{
            width: 400, height: 400, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(242,108,108,0.1) 0%, transparent 70%)',
            bottom: -80, left: -80, zIndex: 0, pointerEvents: 'none',
          }}
        />

        {/* content */}
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row align-items-center min-vh-100 py-5">

            {/* text left */}
            <div className="col-12 col-md-6 text-white">
              {/* eyebrow badge — pulsing yellow dot, links to new AI suite section */}
              <div className="mb-3">
                <a
                  href="#ai-suite"
                  className="hero-badge-link"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'rgba(244, 226, 122, 0.14)',
                    border: '1px solid rgba(244, 226, 122, 0.42)',
                    borderRadius: 999, padding: '6px 14px',
                    fontSize: 12, fontWeight: 600,
                    color: 'var(--soft-yellow, #F4E27A)',
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',
                    textDecoration: 'none',
                    transition: 'background 200ms ease, border-color 200ms ease, transform 200ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(244, 226, 122, 0.22)'
                    e.currentTarget.style.borderColor = 'rgba(244, 226, 122, 0.62)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(244, 226, 122, 0.14)'
                    e.currentTarget.style.borderColor = 'rgba(244, 226, 122, 0.42)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                      background: 'var(--soft-yellow, #F4E27A)',
                      boxShadow: '0 0 0 0 rgba(244, 226, 122, 0.65)',
                      animation: 'ldPulse 1.8s ease-in-out infinite',
                    }}
                  />
                  {t('hero.badge')}
                </a>
              </div>

              <h1 className="display-4 fw-bold mb-4">{t('hero.title')}</h1>
              <p className="lead mb-5 opacity-75">{t('hero.subtitle')}</p>
              <div className="d-flex flex-wrap gap-3 mb-5">
                <a
                  href="/register"
                  className="btn btn-lg px-5 py-3 fw-semibold"
                  style={{ background: 'var(--brand-2)', color: '#0d3a3d', borderRadius: 12 }}
                >
                  {t('hero.cta_primary')}
                </a>
                <a
                  href="#ai-suite"
                  className="btn btn-lg btn-outline-light px-5 py-3 fw-semibold"
                  style={{ borderRadius: 12 }}
                >
                  {t('hero.cta_secondary')}
                </a>
              </div>

              {/* 3 stats */}
              <div className="d-flex flex-wrap gap-4">
                {[
                  { value: t('hero.stat1.value'), label: t('hero.stat1.label') },
                  { value: t('hero.stat2.value'), label: t('hero.stat2.label') },
                  { value: t('hero.stat3.value'), label: t('hero.stat3.label') },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--brand-2)', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* mockup right — pure CSS/SVG, replaces the legacy PNG that had
                untranslated i18n keys leaking into the screenshot */}
            <div className="col-12 col-md-6 d-flex justify-content-center mt-5 mt-md-0">
              <HeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── 2.5. AI SUITE SHOWCASE — new centerpiece for the suite of AI
              tools added in 2026-05. Editorial asymmetric layout. ───── */}
      <AiSuiteSection />

      {/* ── 3. BUILT-ON ROW + LIVE STATS — replaces the old favicon logos
              strip (which leaned on Google's favicon service and implied
              partnerships that don't exist). ───────────────────────── */}
      <BuiltOnRow />
      <LiveStatsBar />

      {/* ── 4. FEATURES 6-CARD GRID (CANVI 3) ───────────────────────────── */}
      <section className="py-5 bg-white" id="features">
        <div className="container">
          <div className="text-center mb-5 ld-section-header">
            <span className="ld-section-label">{t('landing.features_section.label')}</span>
            <h2 className="ld-section-title mt-2">{t('landing.features_section.title')}</h2>
          </div>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="col">
                <div
                  className="ld-feature-card h-100"
                  style={{ animation: `fd-fade-up 0.5s ${0.05 * i}s both` }}
                >
                  <div className="ld-feature-card__icon">
                    <f.Icon size={22} color="var(--brand-1)" />
                  </div>
                  <h3 className="ld-feature-card__title">{t(f.titleKey)}</h3>
                  <p className="ld-feature-card__desc mb-0">{t(f.textKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5–6. VISUAL SECTIONS (CANVI 4) ──────────────────────────────── */}
      <VisualSection
        mockup={<VisualMockupOps />}
        chipKey="visual_a.chip"
        titleKey="visual_a.title"
        textKey="visual_a.text"
        bulletKeys={['visual_a.b1', 'visual_a.b2', 'visual_a.b3']}
      />
      <VisualSection
        mockup={<VisualMockupAi />}
        chipKey="visual_b.chip"
        titleKey="visual_b.title"
        textKey="visual_b.text"
        bulletKeys={['visual_b.b1', 'visual_b.b2', 'visual_b.b3']}
        reverse
      />

      {/* ── 7. HOW IT WORKS (5 steps, Bootstrap) ────────────────────────── */}
      <section className="py-5" id="how" style={{ background: '#f8faf7' }}>
        <div className="container">
          <h2 className="text-center fw-bold mb-5 display-6">
            {t('how_it_works.title')}
          </h2>
          <div className="row row-cols-1 row-cols-md-5 g-3 text-center">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="col">
                <div className="py-3">
                  <div
                    className="fw-bold mb-2"
                    style={{ fontSize: 48, lineHeight: 1, color: 'var(--brand-2)' }}
                  >
                    {i}
                  </div>
                  <div className="fw-semibold mb-1" style={{ fontSize: 15 }}>
                    {t(`step${i}.title`)}
                  </div>
                  <div className="text-muted small">{t(`step${i}.text`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7.5. COMPARISON vs Helium 10 / Sellerboard ─────────────────── */}
      <ComparisonSection />

      {/* ── 8. BUILT FOR — replaces the old stock-photo testimonials with
              honest personas + founder note + beta-status badge ───── */}
      <BuiltForSection />

      {/* ── 9. PRICING ───────────────────────────────────────────────────── */}
      <section className="py-5" id="pricing" style={{ background: '#F6F8F3' }}>
        <div className="container">
          <div className="text-center mb-4">
            <span className="ld-section-label">{t('landing.pricing.label')}</span>
            <h2 className="ld-section-title mt-2">{t('landing.pricing.title')}</h2>
            <p className="ld-section-sub">{t('landing.pricing.subtitle')}</p>
          </div>

          {/* billing toggle */}
          <div className="d-flex justify-content-center align-items-center gap-3 mb-5">
            <span
              className="ld-pricing__toggle-label"
              style={{ fontWeight: !yearly ? 700 : 400 }}
            >
              {t('landing.pricing.monthly')}
            </span>
            <button
              className={`ld-pricing__toggle-switch${yearly ? ' ld-pricing__toggle-switch--on' : ''}`}
              onClick={() => setYearly(y => !y)}
              aria-label="Toggle yearly billing"
            >
              <span className="ld-pricing__toggle-knob" />
            </button>
            <span
              className="ld-pricing__toggle-label"
              style={{ fontWeight: yearly ? 700 : 400 }}
            >
              {t('landing.pricing.yearly')}
            </span>
            {yearly && (
              <span className="ld-pricing__save">{t('landing.pricing.save')}</span>
            )}
          </div>

          <div className="row row-cols-1 row-cols-md-3 g-4">
            {PLANS.map(plan => (
              <div key={plan.code} className="col">
                <div
                  className={`ld-pricing-card h-100${plan.highlight ? ' ld-pricing-card--highlight' : ''}`}
                >
                  {plan.badge && (
                    <div className="ld-pricing-card__badge">{plan.badge}</div>
                  )}
                  <div className="ld-pricing-card__name">{plan.name}</div>
                  <div className="ld-pricing-card__price">
                    <span className="ld-pricing-card__currency">€</span>
                    <span className="ld-pricing-card__amount">
                      {yearly ? plan.yearly : plan.monthly}
                    </span>
                  </div>
                  <div className="ld-pricing-card__period">
                    {t('landing.pricing.per_month')}
                    {yearly ? `, ${t('landing.pricing.billed_yearly')}` : ''}
                  </div>
                  <div className="ld-pricing-card__desc">{plan.desc}</div>
                  <div className="ld-pricing-card__divider" />
                  <ul className="ld-pricing-card__features">
                    {plan.features.map(f => (
                      <li key={f} className="ld-pricing-card__feature">
                        <span className="ld-pricing-card__check">✓</span>{f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className="ld-pricing-card__cta"
                    onClick={() => navigate(plan.code === 'scale' ? '/login' : '/trial')}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9b. CARDS DEL HUB — gestionades des de l'admin de Freedolia ── */}
      <HubCards />

      {/* ── 10. FAQ ──────────────────────────────────────────────────────── */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <span className="ld-section-label">{t('landing.faq.label')}</span>
            <h2 className="ld-section-title mt-2">{t('landing.faq.title')}</h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="ld-faq__list">
                {FAQS.map((faq, i) => (
                  <div
                    key={i}
                    className={`ld-faq__item${openFaq === i ? ' ld-faq__item--open' : ''}`}
                  >
                    <button
                      className="ld-faq__question"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    >
                      {faq.q}
                      <span className="ld-faq__chevron">▾</span>
                    </button>
                    <div className="ld-faq__answer">{faq.a}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 10.5. TRUST BAR — risk-reversal pills above the final CTA. ─── */}
      <section style={{ padding: '24px 0 0' }}>
        <div className="container">
          <div style={{
            maxWidth: 980, margin: '0 auto',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
            gap: 12,
          }}>
            {[
              { iconBg: 'rgba(63,191,154,0.16)', iconColor: 'var(--success-ink, #2B7A66)', icon: '✓', key: 'landing.trust.gdpr',   defaultText: 'GDPR · Art. 17 + 20' },
              { iconBg: 'rgba(110,203,195,0.18)', iconColor: 'var(--brand-1, #1F5F63)',     icon: '⚡', key: 'landing.trust.byok',   defaultText: 'BYOK · cap cost ocult' },
              { iconBg: 'rgba(244,226,122,0.20)', iconColor: '#7A5F22',                      icon: '↺', key: 'landing.trust.cancel', defaultText: 'Cancel·la quan vulguis' },
              { iconBg: 'rgba(31,95,99,0.10)',    iconColor: 'var(--brand-1, #1F5F63)',     icon: '🔒', key: 'landing.trust.stripe', defaultText: 'Stripe live · pagaments segurs' },
            ].map((chip, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 14px 8px 10px', borderRadius: 999,
                background: 'var(--surface-bg, #fff)',
                border: '1px solid rgba(31,95,99,0.12)',
                boxShadow: '0 4px 12px rgba(15,36,38,0.05)',
                fontSize: 13, fontWeight: 600, color: '#243333',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '50%',
                  background: chip.iconBg, color: chip.iconColor,
                  fontSize: 12, fontWeight: 700,
                }}>{chip.icon}</span>
                {t(chip.key, chip.defaultText)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 11. FINAL CTA ────────────────────────────────────────────────── */}
      <section className="ld-cta-section">
        <span className="ld-section-label">{t('landing.cta.label')}</span>
        <h2 className="ld-section-title">{t('landing.final_cta.title')}</h2>
        <p className="ld-section-sub">{t('landing.final_cta.description')}</p>
        <div className="ld-cta-section__btns">
          <button className="ld-btn ld-btn--primary-dark" onClick={() => navigate('/trial')}>
            {t('landing.final_cta.cta_primary')}
          </button>
          <button className="ld-btn ld-btn--outline-dark" onClick={() => navigate('/login')}>
            {t('landing.nav.login')}
          </button>
        </div>
        <p className="ld-cta-section__note">{t('landing.cta.note')}</p>
      </section>

      {/* ── 12. FOOTER ───────────────────────────────────────────────────── */}
      <footer className="ld-footer">
        <div className="container">
          <div className="row g-4 ld-footer__top">
            <div className="col-12 col-md-4">
              <img
                src="/brand/freedoliapp/logo/logo_white.png"
                alt="Freedoliapp"
                style={{ height: 28, marginBottom: 10 }}
              />
              <p className="ld-footer__brand-desc">{t('landing.footer.brand_desc')}</p>
            </div>
            <div className="col-6 col-md-2">
              <div className="ld-footer__col-title">{t('landing.footer.product')}</div>
              <ul className="ld-footer__links">
                <li><a href="#features" className="ld-footer__link">{t('landing.footer.features')}</a></li>
                <li><a href="#pricing"  className="ld-footer__link">{t('landing.footer.pricing')}</a></li>
                <li><a href="#how"      className="ld-footer__link">{t('landing.nav.how')}</a></li>
              </ul>
            </div>
            <div className="col-6 col-md-2">
              <div className="ld-footer__col-title">{t('landing.footer.account')}</div>
              <ul className="ld-footer__links">
                <li><Link to="/trial" className="ld-footer__link">{t('landing.footer.free_trial')}</Link></li>
                <li><Link to="/login" className="ld-footer__link">{t('landing.nav.login')}</Link></li>
              </ul>
            </div>
            <div className="col-6 col-md-2">
              <div className="ld-footer__col-title">{t('landing.footer.legal')}</div>
              <ul className="ld-footer__links">
                <li><Link to="/privacy" className="ld-footer__link">{t('landing.footer.privacy')}</Link></li>
                <li><Link to="/terms"   className="ld-footer__link">{t('landing.footer.terms')}</Link></li>
                <li><Link to="/cookies" className="ld-footer__link">{t('landing.footer.cookies')}</Link></li>
              </ul>
            </div>
            <div className="col-6 col-md-2">
              <div className="ld-footer__col-title">Freedolia</div>
              <ul className="ld-footer__links">
                {FREEDOLIA_LINKS.map(({ key, path }) => (
                  <li key={key}>
                    <a href={`https://freedolia.com/${fdLang}${path ? '/' + path : ''}`} className="ld-footer__link" target="_blank" rel="noopener">
                      {t(`landing.footer.${key}`)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="ld-footer__bottom">
            <span>© {new Date().getFullYear()} Freedolia SL</span>
            <span>{t('landing.footer.rights')}</span>
          </div>
        </div>
      </footer>

    </div>
  )
}
